import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  EmptyState,
  ErrorState,
  LoadingBlock,
  PageHeading,
} from "@/components/states";
import { useAuth } from "@/context/AuthContext";
import { formatCurrency, formatDate } from "@/lib/product";
import { useApiError } from "@/lib/useApiError";

const MijnBestellingen = () => {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const { bestellingen } = useAuth();
  const navigate = useNavigate();
  const toMessage = useApiError();

  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const data = await bestellingen();
      setOrders(Array.isArray(data) ? data : []);
    } catch (err) {
      setError(toMessage(err));
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bestellingen]);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <div className="mx-auto max-w-5xl px-4 py-10 sm:px-6 lg:px-8" data-testid="orders-page">
      <PageHeading
        title={t("orders.title")}
        subtitle={t("orders.subtitle")}
        testId="orders-heading"
        actions={
          <Button variant="outline" onClick={load} data-testid="orders-refresh-button">
            <RefreshCw className="mr-2 h-4 w-4" aria-hidden="true" />
            {t("orders.refresh")}
          </Button>
        }
      />

      <div className="mt-6">
        {loading ? (
          <LoadingBlock />
        ) : error ? (
          <ErrorState message={error} onRetry={load} />
        ) : orders.length === 0 ? (
          <EmptyState
            title={t("orders.empty")}
            actionLabel={t("orders.emptyAction")}
            onAction={() => navigate("/producten")}
          />
        ) : (
          <>
            <p className="text-sm text-muted-foreground" data-testid="orders-count">
              {t("orders.count", { count: orders.length })}
            </p>

            {/* Desktop table */}
            <Card className="mt-4 hidden overflow-hidden md:block">
              <Table data-testid="orders-table">
                <TableHeader>
                  <TableRow>
                    <TableHead>{t("orders.ordernummer")}</TableHead>
                    <TableHead>{t("orders.datum")}</TableHead>
                    <TableHead>{t("orders.fase")}</TableHead>
                    <TableHead className="text-right">{t("orders.bedrag")}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {orders.map((order, index) => (
                    <TableRow key={order.ordernummer || order.id || index} data-testid="orders-table-row">
                      <TableCell className="font-medium">
                        <span data-testid="order-ordernummer">{order.ordernummer || "—"}</span>
                        {order.klant_referentie && (
                          <p className="mt-1 text-xs font-medium" data-testid="order-klant-referentie">{t("orders.referentie")}: {order.klant_referentie}</p>
                        )}
                        {order.opmerking && (
                          <p className="mt-1 max-w-[280px] truncate text-xs text-muted-foreground">
                            {order.opmerking}
                          </p>
                        )}
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground">
                        {formatDate(order.datum, lang)}
                      </TableCell>
                      <TableCell>
                        <Badge variant="secondary" className="whitespace-normal">
                          {order.fase || t("common.unknown")}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right tabular-nums">
                        {formatCurrency(order.bedrag, lang)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Card>

            {/* Mobile cards */}
            <div className="mt-4 space-y-3 md:hidden" data-testid="orders-card-list">
              {orders.map((order, index) => (
                <Card key={order.ordernummer || order.id || index} className="p-4">
                  <div className="flex items-start justify-between gap-3">
                    <span className="font-medium" data-testid="order-ordernummer">
                      {order.ordernummer || "—"}
                    </span>
                    <Badge variant="secondary" className="whitespace-normal">
                      {order.fase || t("common.unknown")}
                    </Badge>
                  </div>
                  <dl className="mt-3 grid grid-cols-2 gap-2 text-sm">
                    <dt className="text-muted-foreground">{t("orders.datum")}</dt>
                    <dd className="text-right">{formatDate(order.datum, lang)}</dd>
                    <dt className="text-muted-foreground">{t("orders.bedrag")}</dt>
                    <dd className="text-right font-semibold tabular-nums">
                      {formatCurrency(order.bedrag, lang)}
                    </dd>
                  </dl>
                  {order.klant_referentie && (
                    <p className="mt-3 text-xs font-medium">{t("orders.referentie")}: {order.klant_referentie}</p>
                  )}
                  {order.opmerking && (
                    <p className="mt-3 text-xs text-muted-foreground">
                      {t("orders.opmerking")}: {order.opmerking}
                    </p>
                  )}
                </Card>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default MijnBestellingen;
